import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import debounce from 'lodash-es/debounce';

import { TextInput } from '@dojo/widgets/text-input';
import { Icon } from '@dojo/widgets/icon';

// make generated .d.ts file valid
import {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	WNodeFactory,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	WidgetProperties,
	DNode,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	DefaultChildrenWNodeFactory,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	I18nProperties
} from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { nls } from '../support/glossary-nls';
import { UsedLetters } from '../support/svc';

export interface ToolbarProperties {
	usedletters: UsedLetters;
	selected: string | null | undefined;
	themedCss: Record<string, string>;
	select_letter: (ltr: string) => Promise<void>;
	do_search: (search: string) => Promise<void>;
}

interface ToolbarIcache {
	used: Set<string>;
	search: string;
}

const icache = createICacheMiddleware<ToolbarIcache>();
const factory = create({ icache, nls }).properties<ToolbarProperties>();

export const Toolbar = factory(({ properties, middleware: { icache, nls } }) => {
	const {
		messages: { alphabet, numbers, search }
	} = nls();
	const { usedletters, selected, themedCss, select_letter, do_search } = properties();
	const used = icache.getOrSet('used', () => new Set<string>(Object.keys(usedletters)));

	const buttons: DNode[] = Array.from(alphabet).map((letter) => {
		const isSelected = letter === selected;
		const isInvalid = !used.has(letter);
		return v(
			'button',
			{
				classes: [
					themedCss.alphabutton,
					themedCss.touch,
					isSelected ? themedCss.selectedbutton : null,
					isInvalid ? themedCss.invalidbutton : null
				],
				disabled: isInvalid,
				...formatAriaProperties({ pressed: `${isSelected}` }),
				onclick: (event) => {
					event && event.stopPropagation();
					!isInvalid && select_letter(letter);
					icache.set('search', '');
				}
			},
			[letter]
		);
	});
	buttons.push(
		v(
			'button',
			{
				classes: [themedCss.numberbutton, themedCss.touch, themedCss.invalidbutton],
				disabled: true,
				...formatAriaProperties({ pressed: 'false' }),
				onclick: (event) => {
					event && event.stopPropagation();
					// this.select_letter('numbers');
				}
			},
			[numbers]
		),
		w(
			TextInput,
			{
				kind: 'outlined',
				placeholder: search,
				value: icache.getOrSet('search', ''),
				onValue: (val: string) => {
					icache.set('search', val);
					if (val.length >= 3) {
						debounce(() => do_search(val), 500)();
					}
				}
			},
			[
				{
					label: search,
					trailing: w(Icon, { type: 'searchIcon' })
				}
			]
		)
	);

	return v('div', { classes: [themedCss.toolbar] }, buttons);
});
